import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { clearPublicSettingsCache } from "@/lib/publicSettings";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Phone, Share2, Mail as MailIcon, DollarSign, Percent,
  Search as SearchIcon, LineChart, Shield, KeyRound, Save, Plus, X,
} from "lucide-react";

const SECTIONS = [
  { key: "company", label: "Company Information", icon: Building2 },
  { key: "contact", label: "Contact Details", icon: Phone },
  { key: "social", label: "Social Media", icon: Share2 },
  { key: "email", label: "Email Settings", icon: MailIcon },
  { key: "currency", label: "Currency", icon: DollarSign },
  { key: "tax", label: "Tax", icon: Percent },
  { key: "seo", label: "SEO", icon: SearchIcon },
  { key: "analytics", label: "Analytics", icon: LineChart },
  { key: "integrations", label: "Integrations & Secrets", icon: KeyRound, roles: ["super_admin"] },
];

const inputCls = "rounded-none h-11 mt-1";
const labelCls = "overline text-muted-foreground text-[10px]";

const ArrayInput = ({ label, values, onChange, placeholder, testId }) => {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...(values || []), v.trim()]); setV(""); } };
  return (
    <div>
      <Label className={labelCls}>{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input data-testid={`${testId}-input`} className="rounded-none h-11" value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button type="button" onClick={add} className="rounded-none h-11 bg-primary" data-testid={`${testId}-add`}><Plus size={14} /></Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {(values || []).map((x) => (
          <span key={x} data-testid={`${testId}-item`} className="inline-flex items-center gap-2 border border-border px-2 py-1 text-xs bg-background">
            {x}
            <button type="button" onClick={() => onChange((values || []).filter((y) => y !== x))} data-testid={`${testId}-remove`}><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [all, setAll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initial = searchParams.get("section") || "company";
  const [tab, setTab] = useState(SECTIONS.some((s) => s.key === initial) ? initial : "company");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/all");
        setAll(data);
      } catch (e) {
        toast.error("Failed to load settings", { description: e.response?.data?.detail || "" });
      } finally {
        setLoading(false);
      }
    })();
    // Load once on mount — `api` is a stable module import; toast is a stable
    // module helper. Adding them to deps would not re-fire meaningfully.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSection = (section, patch) => {
    setAll((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...patch } }));
  };

  const saveSection = async (section) => {
    if (!all?.[section]) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/settings/${section}`, { data: all[section] });
      setAll(data);
      clearPublicSettingsCache();
      toast.success(`${section} saved`);
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const switchTab = (k) => {
    setTab(k);
    const p = new URLSearchParams(searchParams);
    p.set("section", k);
    setSearchParams(p);
  };

  const active = useMemo(() => SECTIONS.find((s) => s.key === tab), [tab]);

  if (loading) return <AdminLayout title="Settings"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  if (!all) return <AdminLayout title="Settings"><div className="text-muted-foreground">No settings available.</div></AdminLayout>;

  return (
    <AdminLayout
      title="Website Settings"
      actions={
        <Button data-testid="save-section-btn" onClick={() => saveSection(tab)} disabled={saving} className="rounded-none bg-accent hover:bg-accent/90">
          <Save size={14} className="mr-2" /> {saving ? "Saving…" : `Save ${active?.label}`}
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <div className="border border-border bg-card p-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const activeCls = tab === s.key ? "bg-accent text-accent-foreground" : "hover:bg-background";
            return (
              <button
                key={s.key}
                data-testid={`settings-tab-${s.key}`}
                onClick={() => switchTab(s.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${activeCls}`}
              >
                <Icon size={14} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="border border-border bg-card p-8">
          {tab === "company" && (
            <div data-testid="section-company" className="space-y-4">
              <SectionHeader label="Company Information" desc="Used across the site header, footer, and email templates." />
              <TwoCol>
                <Field label="Company name"><Input data-testid="cmp-name" className={inputCls} value={all.company?.name || ""} onChange={(e) => setSection("company", { name: e.target.value })} /></Field>
                <Field label="Tagline"><Input data-testid="cmp-tagline" className={inputCls} value={all.company?.tagline || ""} onChange={(e) => setSection("company", { tagline: e.target.value })} /></Field>
              </TwoCol>
              <Field label="Logo URL">
                <Input data-testid="cmp-logo" className={inputCls} placeholder="https://…/logo.png" value={all.company?.logo_url || ""} onChange={(e) => setSection("company", { logo_url: e.target.value })} />
                {all.company?.logo_url && <img src={all.company.logo_url} alt="logo" className="mt-3 h-14 border border-border" />}
              </Field>
              <Field label="About (short)"><Textarea data-testid="cmp-about" rows={3} className="rounded-none mt-1" value={all.company?.about_short || ""} onChange={(e) => setSection("company", { about_short: e.target.value })} /></Field>
            </div>
          )}

          {tab === "contact" && (
            <div data-testid="section-contact" className="space-y-4">
              <SectionHeader label="Contact Details" desc="Address, phones, emails, and business hours shown on public pages." />
              <TwoCol>
                <ArrayInput testId="contact-emails" label="Email addresses" values={all.contact?.emails} onChange={(v) => setSection("contact", { emails: v })} placeholder="hello@company.com" />
                <ArrayInput testId="contact-phones" label="Phone numbers" values={all.contact?.phones} onChange={(v) => setSection("contact", { phones: v })} placeholder="+1 555…" />
              </TwoCol>
              <Field label="Address line 1"><Input data-testid="contact-addr1" className={inputCls} value={all.contact?.address_line1 || ""} onChange={(e) => setSection("contact", { address_line1: e.target.value })} /></Field>
              <Field label="Address line 2"><Input data-testid="contact-addr2" className={inputCls} value={all.contact?.address_line2 || ""} onChange={(e) => setSection("contact", { address_line2: e.target.value })} /></Field>
              <TwoCol>
                <Field label="City"><Input data-testid="contact-city" className={inputCls} value={all.contact?.city || ""} onChange={(e) => setSection("contact", { city: e.target.value })} /></Field>
                <Field label="State / Region"><Input data-testid="contact-state" className={inputCls} value={all.contact?.state || ""} onChange={(e) => setSection("contact", { state: e.target.value })} /></Field>
              </TwoCol>
              <TwoCol>
                <Field label="Postal / ZIP"><Input data-testid="contact-postal" className={inputCls} value={all.contact?.postal || ""} onChange={(e) => setSection("contact", { postal: e.target.value })} /></Field>
                <Field label="Country"><Input data-testid="contact-country" className={inputCls} value={all.contact?.country || ""} onChange={(e) => setSection("contact", { country: e.target.value })} /></Field>
              </TwoCol>
              <div>
                <Label className={labelCls}>Business hours</Label>
                <div className="mt-2 border border-border">
                  {(all.contact?.business_hours || []).map((h, i) => (
                    <div key={h.day} className="grid grid-cols-[110px_1fr_1fr_100px] gap-2 items-center px-3 py-2 border-b border-border last:border-b-0">
                      <div className="text-sm">{h.day}</div>
                      <Input disabled={h.closed} data-testid={`hours-${h.day}-open`} className="rounded-none h-9" type="time" value={h.open} onChange={(e) => {
                        const rows = [...all.contact.business_hours];
                        rows[i] = { ...rows[i], open: e.target.value };
                        setSection("contact", { business_hours: rows });
                      }} />
                      <Input disabled={h.closed} data-testid={`hours-${h.day}-close`} className="rounded-none h-9" type="time" value={h.close} onChange={(e) => {
                        const rows = [...all.contact.business_hours];
                        rows[i] = { ...rows[i], close: e.target.value };
                        setSection("contact", { business_hours: rows });
                      }} />
                      <label className="flex items-center gap-2 text-xs">
                        <Checkbox checked={h.closed} data-testid={`hours-${h.day}-closed`} onCheckedChange={(v) => {
                          const rows = [...all.contact.business_hours];
                          rows[i] = { ...rows[i], closed: !!v };
                          setSection("contact", { business_hours: rows });
                        }} />
                        Closed
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "social" && (
            <div data-testid="section-social" className="space-y-4">
              <SectionHeader label="Social Media" desc="Public profile URLs shown in the site footer." />
              {["facebook", "instagram", "linkedin", "youtube", "twitter", "pinterest"].map((k) => (
                <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                  <Input data-testid={`social-${k}`} className={inputCls} placeholder={`https://…/${k}`} value={all.social?.[k] || ""} onChange={(e) => setSection("social", { [k]: e.target.value })} />
                </Field>
              ))}
            </div>
          )}

          {tab === "email" && (
            <div data-testid="section-email" className="space-y-4">
              <SectionHeader label="Email Settings" desc="Sender identity, notification recipients, and auto-acknowledgement copy." />
              <TwoCol>
                <Field label="Sender email"><Input data-testid="email-sender" className={inputCls} value={all.email?.sender_email || ""} onChange={(e) => setSection("email", { sender_email: e.target.value })} /></Field>
                <Field label="Notification email (primary)"><Input data-testid="email-notif-primary" className={inputCls} value={all.email?.notification_email_primary || ""} onChange={(e) => setSection("email", { notification_email_primary: e.target.value })} /></Field>
              </TwoCol>
              <ArrayInput testId="email-notif-cc" label="Notification CC" values={all.email?.notification_email_cc} onChange={(v) => setSection("email", { notification_email_cc: v })} placeholder="cc@company.com" />
              <div className="flex items-center gap-3">
                <Checkbox data-testid="email-ack-enabled" checked={!!all.email?.auto_ack_enabled} onCheckedChange={(v) => setSection("email", { auto_ack_enabled: !!v })} />
                <Label className="text-sm">Send automatic acknowledgement email on enquiries</Label>
              </div>
              <Field label="Acknowledgement message"><Textarea data-testid="email-ack-msg" rows={4} className="rounded-none mt-1" value={all.email?.auto_ack_message || ""} onChange={(e) => setSection("email", { auto_ack_message: e.target.value })} /></Field>
            </div>
          )}

          {tab === "currency" && (
            <div data-testid="section-currency" className="space-y-4">
              <SectionHeader label="Currency" desc="Site default currency and available currencies toggle." />
              <TwoCol>
                <Field label="Default currency">
                  <Select value={all.currency?.default || "usd"} onValueChange={(v) => setSection("currency", { default: v })}>
                    <SelectTrigger data-testid="currency-default" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="usd">USD ($)</SelectItem><SelectItem value="inr">INR (₹)</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="USD → INR rate"><Input data-testid="currency-rate" type="number" step="0.01" className={inputCls} value={all.currency?.usd_to_inr_rate || 0} onChange={(e) => setSection("currency", { usd_to_inr_rate: Number(e.target.value) })} /></Field>
              </TwoCol>
              <ArrayInput testId="currency-supported" label="Supported currencies" values={all.currency?.supported} onChange={(v) => setSection("currency", { supported: v })} placeholder="usd" />
            </div>
          )}

          {tab === "tax" && (
            <div data-testid="section-tax" className="space-y-4">
              <SectionHeader label="Tax" desc="Toggle tax and configure rules by region (advanced rules configurable via API for now)." />
              <div className="flex items-center gap-3">
                <Checkbox data-testid="tax-enabled" checked={!!all.tax?.enabled} onCheckedChange={(v) => setSection("tax", { enabled: !!v })} />
                <Label className="text-sm">Enable tax collection on checkout</Label>
              </div>
              <p className="text-xs text-muted-foreground">Region-specific tax rules will ship in Phase 5. For now, checkout runs tax-free.</p>
            </div>
          )}

          {tab === "seo" && (
            <div data-testid="section-seo" className="space-y-4">
              <SectionHeader label="SEO" desc="Default site meta tags." />
              <Field label="Default title"><Input data-testid="seo-title" className={inputCls} value={all.seo?.default_title || ""} onChange={(e) => setSection("seo", { default_title: e.target.value })} /></Field>
              <Field label="Default description"><Textarea data-testid="seo-desc" rows={3} className="rounded-none mt-1" value={all.seo?.default_description || ""} onChange={(e) => setSection("seo", { default_description: e.target.value })} /></Field>
              <Field label="OG image URL"><Input data-testid="seo-og" className={inputCls} value={all.seo?.og_image_url || ""} onChange={(e) => setSection("seo", { og_image_url: e.target.value })} /></Field>
              <ArrayInput testId="seo-keywords" label="Keywords" values={all.seo?.keywords} onChange={(v) => setSection("seo", { keywords: v })} placeholder="house plans" />
            </div>
          )}

          {tab === "analytics" && (
            <div data-testid="section-analytics" className="space-y-4">
              <SectionHeader label="Analytics" desc="Tracking IDs — pasted into the public site head." />
              <TwoCol>
                <Field label="Google Analytics 4 ID"><Input data-testid="ga4" className={inputCls} placeholder="G-XXXXXXX" value={all.analytics?.ga4_id || ""} onChange={(e) => setSection("analytics", { ga4_id: e.target.value })} /></Field>
                <Field label="Plausible domain"><Input data-testid="plausible" className={inputCls} placeholder="dreamhomeengineers.com" value={all.analytics?.plausible_domain || ""} onChange={(e) => setSection("analytics", { plausible_domain: e.target.value })} /></Field>
              </TwoCol>
              <Field label="Google Tag Manager ID"><Input data-testid="gtm" className={inputCls} placeholder="GTM-XXXXXX" value={all.analytics?.gtm_id || ""} onChange={(e) => setSection("analytics", { gtm_id: e.target.value })} /></Field>
            </div>
          )}

          {tab === "integrations" && (
            <div data-testid="section-integrations" className="space-y-6">
              <SectionHeader label="Integrations & Secrets" desc="API keys used by the platform. Secrets are stored server-side and masked on read." icon={Shield} />
              <div className="text-xs bg-background border border-border p-3 text-muted-foreground">
                <strong>Note:</strong> Some integration changes require a backend restart to fully take effect. Resend key & sender are hot-reloaded.
              </div>
              {[
                { key: "stripe", label: "Stripe", fields: [["publishable_key", "Publishable key"], ["secret_key", "Secret key", true]] },
                { key: "razorpay", label: "Razorpay", fields: [["key_id", "Key ID"], ["key_secret", "Key secret", true]] },
                { key: "turnstile", label: "Cloudflare Turnstile", fields: [["site_key", "Site key"], ["secret", "Secret", true]] },
                { key: "resend", label: "Resend (email)", fields: [["sender", "From address"], ["api_key", "API key", true]] },
              ].map((prov) => (
                <div key={prov.key} className="border border-border p-4" data-testid={`integrations-${prov.key}`}>
                  <div className="font-medium mb-3">{prov.label}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {prov.fields.map(([fk, flabel, isSecret]) => (
                      <Field key={fk} label={flabel + (isSecret ? " (secret)" : "")}>
                        <Input
                          data-testid={`integ-${prov.key}-${fk}`}
                          className={inputCls}
                          type={isSecret ? "password" : "text"}
                          placeholder={isSecret ? "Paste to update — leave masked to keep" : ""}
                          value={all.integrations?.[prov.key]?.[fk] || ""}
                          onChange={(e) => setSection("integrations", { [prov.key]: { ...(all.integrations?.[prov.key] || {}), [fk]: e.target.value } })}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const SectionHeader = ({ label, desc, icon: Icon }) => (
  <div className="mb-4 pb-3 border-b border-border">
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-accent" />}
      <div className="overline text-accent">{label}</div>
    </div>
    {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className={labelCls}>{label}</Label>
    {children}
  </div>
);

const TwoCol = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

export default SettingsPage;
